import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

  const submitRequest = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("Les deux mots de passe ne correspondent pas"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(t("Mot de passe modifié — connecte-toi !"));
      navigate("/connexion");
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reset-password-page" className="mx-auto flex max-w-md flex-col items-center px-4 py-16 sm:py-24">
      <Seo title={t("Mot de passe oublié — GoSpot")} siteName="GoSpot" description={t("Réinitialise ton mot de passe GoSpot.")} />
      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4]">
        <MapPin className="h-6 w-6 text-white" />
      </div>

      {token ? (
        <>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{t("Nouveau mot de passe")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("Choisis ton nouveau mot de passe GoSpot.")}</p>
          <form onSubmit={submitReset} className="mt-6 w-full space-y-3">
            <input
              data-testid="reset-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Nouveau mot de passe (6 caractères min.)")}
              required
              minLength={6}
              className={inputClass}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={t("Confirme le mot de passe")}
              required
              minLength={6}
              className={inputClass}
            />
            {error && (
              <p data-testid="reset-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="reset-submit-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4" />
              {loading ? t("Un instant…") : t("Modifier mon mot de passe")}
            </button>
          </form>
        </>
      ) : done ? (
        <div className="flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
            <MailCheck className="h-7 w-7 text-emerald-500" />
          </span>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900">{t("Vérifie ta boîte courriel")}</h1>
          <p data-testid="reset-sent-message" className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
            {t("Si un compte existe avec cette adresse, un courriel avec un lien de réinitialisation vient d'être envoyé. Le lien est valide 30 minutes.")}
          </p>
          <Link to="/connexion" data-testid="reset-back-login" className="mt-6 text-sm font-semibold text-[#2563EB] hover:underline">
            {t("Retour à la connexion")}
          </Link>
        </div>
      ) : (
        <>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{t("Mot de passe oublié ?")}</h1>
          <p className="mt-2 text-center text-sm text-slate-500">
            {t("Entre ton courriel et on t'envoie un lien pour réinitialiser ton mot de passe.")}
          </p>
          <form onSubmit={submitRequest} className="mt-6 w-full space-y-3">
            <input
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("Courriel")}
              required
              className={inputClass}
            />
            {error && (
              <p data-testid="forgot-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="forgot-submit-button"
              disabled={loading}
              className="w-full rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? t("Un instant…") : t("Envoyer le lien")}
            </button>
          </form>
          <Link to="/connexion" className="mt-6 text-sm font-semibold text-[#2563EB] hover:underline">
            {t("Retour à la connexion")}
          </Link>
        </>
      )}
    </main>
  );
}
